import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ThemeMode } from "../theme/theme";

const STORAGE_KEY = "alfin_theme_mode";

interface ThemeModeContextType {
  mode: ThemeMode;
  toggleMode: () => void;
  setMode: (mode: ThemeMode) => void;
}

const ThemeModeContext = createContext<ThemeModeContextType | undefined>(
  undefined
);

/**
 * Начальный режим: сначала пользовательский выбор из localStorage, затем
 * тема Telegram, иначе fallback на dark.
 */
function resolveInitialMode(): ThemeMode {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "light" || saved === "dark") return saved;
  } catch {
    // localStorage может быть недоступен
  }

  const tgScheme = Telegram?.WebApp?.colorScheme;
  if (tgScheme === "light" || tgScheme === "dark") return tgScheme;

  return "dark";
}

export function ThemeModeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(resolveInitialMode);

  const persist = useCallback((next: ThemeMode) => {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // молча игнорируем — предпочтение просто не сохранится
    }
  }, []);

  const setMode = useCallback(
    (next: ThemeMode) => {
      setModeState(next);
      persist(next);
    },
    [persist]
  );

  const toggleMode = useCallback(() => {
    setModeState((prev) => {
      const next: ThemeMode = prev === "dark" ? "light" : "dark";
      persist(next);
      return next;
    });
  }, [persist]);

  // Синхронизируем цвет фона Telegram с активной темой
  useEffect(() => {
    const bg = mode === "dark" ? "#0F0F12" : "#F5F5F7";
    try {
      Telegram.WebApp.setHeaderColor(bg);
      Telegram.WebApp.setBackgroundColor(bg);
    } catch {
      // старые версии Telegram могут не поддерживать
    }
  }, [mode]);

  const value = useMemo(
    () => ({ mode, toggleMode, setMode }),
    [mode, toggleMode, setMode]
  );

  return (
    <ThemeModeContext.Provider value={value}>
      {children}
    </ThemeModeContext.Provider>
  );
}

export function useThemeMode(): ThemeModeContextType {
  const ctx = useContext(ThemeModeContext);
  if (!ctx) {
    throw new Error("useThemeMode must be used within ThemeModeProvider");
  }
  return ctx;
}
